import { ProductTypeValue } from '@app/data-stores/ptr/types';
import { DeepReadonly } from '@app/types';
import type { InventoryItem } from '@app/types/shop-bff/types';
import { FieldSet, Form } from '@redbubble/design-system';
import { difference, isEqual, omit, without } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import type {
  Gender,
  MeasurementUnit,
  SizeGuideData,
} from '../../../../components/SizeGuide/types';
import type { CurrentConfiguratorValues } from '../../hooks/usePTRProductConfigurator';
import {
  FAMILY_OPTION_PREFIX,
  PRODUCT_OPTION_PREFIX,
} from '../../utils/ptrConfiguratorUtils';
export const CONFIGURATOR_FORM_ID = 'product-configurator-form';

import { usePrefersReducedMotion } from '@app/hooks/usePrefersReducedMotion';
import { OptionFieldDelegator } from './OptionFieldDelegator';
import {
  DSFormChangeType,
  DSFormOnChangeCallback,
  FormikValues,
} from './types';
import { useConfiguratorViewportProfile } from './useConfiguratorViewportProfile';
import { ConfiguratorOption, ProductFamilyValue } from './utils';

const useScrollValidationErrorsIntoView = () => {
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const errorKey = Object.keys(validationErrors).find(Boolean);

    const fieldSetComponent =
      errorKey &&
      document.getElementById(`${CONFIGURATOR_FORM_ID}_${errorKey}`);

    if (!fieldSetComponent) return;

    fieldSetComponent.scrollIntoView?.({
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
      block: 'center',
    });
  }, [prefersReducedMotion, validationErrors]);

  return setValidationErrors;
};

const getFieldsRequiringATCGuard = (
  productType: DeepReadonly<ProductTypeValue>,
  productFamily?: ProductFamilyValue | null
): string[] => {
  const atcGuard = productType.options
    .filter((option) => !option.preselect)
    .map((option) => `${PRODUCT_OPTION_PREFIX}${option.key}`);
  const atcGuardFamily =
    productFamily?.options
      .filter((option) => !option.preselect)
      .map((option) => `${FAMILY_OPTION_PREFIX}${option.key}`) || [];
  return [...atcGuard, ...atcGuardFamily];
};

export interface ProductConfiguratorProps {
  configuratorOptions: ConfiguratorOption[];
  productType: DeepReadonly<ProductTypeValue>;
  productFamily: ProductFamilyValue | null;
  currentInventoryItem: InventoryItem;
  currentValues: CurrentConfiguratorValues;
  onSubmit: () => Promise<void>;
  updateForm: DSFormOnChangeCallback;
  locale: string;
  showSizeGuideButton?: boolean;
  sizeGuideData?: SizeGuideData;
  initialGender?: Gender;
  initialMeasurementUnit?: MeasurementUnit;
}

export const ProductConfigurator: React.FC<ProductConfiguratorProps> = ({
  configuratorOptions,
  productType,
  productFamily,
  currentInventoryItem,
  currentValues,
  onSubmit,
  updateForm,
  locale,
  sizeGuideData,
  initialGender,
  initialMeasurementUnit,
}) => {
  const viewportProfile = useConfiguratorViewportProfile();
  const setValidationErrors = useScrollValidationErrorsIntoView();

  const [fieldsRequiringATCGuard, setFieldsRequiringATCGuard] = useState(() =>
    getFieldsRequiringATCGuard(productType, productFamily)
  );

  const handleChange = (
    formState: DSFormChangeType,
    prevFormState: DSFormChangeType
  ) => {
    if (isEqual(formState.values, prevFormState.values)) return;

    const atcFieldsChanged = Object.keys(formState.values).filter((key) =>
      fieldsRequiringATCGuard.includes(key)
    );

    if (atcFieldsChanged.length > 0) {
      setFieldsRequiringATCGuard((prev) => without(prev, ...atcFieldsChanged));
    }

    const changedKeys = difference(
      Object.keys(formState.values),
      Object.keys(prevFormState.values)
    );

    const excludeFromAnalytics = changedKeys.some(
      (changedKey) => !fieldsRequiringATCGuard.includes(changedKey)
    );

    updateForm(formState, prevFormState, excludeFromAnalytics);
  };

  const omittedValues = useMemo(() => {
    const fieldsToOmit = fieldsRequiringATCGuard;
    return {
      productOptionsValues: omit(
        currentValues.prefixedProductOptionsValues,
        fieldsToOmit
      ),
      familyOptionsValues: omit(
        currentValues.prefixedFamilyOptionsValues,
        fieldsToOmit
      ),
    };
  }, [fieldsRequiringATCGuard, currentValues]);

  const initialValues = {
    ...omittedValues.productOptionsValues,
    ...omittedValues.familyOptionsValues,
  };

  return (
    <Form
      enableReinitialize
      formId={CONFIGURATOR_FORM_ID}
      initialValues={initialValues}
      onChange={handleChange}
      onSubmit={(_, { setSubmitting }) => {
        return onSubmit().then(() => setSubmitting(false));
      }}
      profile={viewportProfile}
      validate={(values: FormikValues): Record<string, string> => {
        const errors: Record<string, string> = {};

        fieldsRequiringATCGuard.forEach((fieldRequiringATCGuard) => {
          if (!values[fieldRequiringATCGuard]) {
            errors[fieldRequiringATCGuard] = 'Required';
          }
        });

        setValidationErrors(errors);

        return errors;
      }}
      validateOnBlur={false}
      validateOnChange={false}
      validateOnSubmit
    >
      {({ errors }) => (
        <ProductConfiguratorFields
          configuratorOptions={configuratorOptions}
          currentInventoryItem={currentInventoryItem}
          currentValues={currentValues}
          locale={locale}
          errors={errors}
          sizeGuideData={sizeGuideData}
          initialGender={initialGender}
          initialMeasurementUnit={initialMeasurementUnit}
        />
      )}
    </Form>
  );
};

export interface ProductConfiguratorFieldsProps {
  configuratorOptions: ConfiguratorOption[];
  currentInventoryItem?: InventoryItem;
  currentValues: CurrentConfiguratorValues;
  locale: string;
  errors: Record<string, string>;
  sizeGuideData?: SizeGuideData;
  initialGender?: Gender;
  initialMeasurementUnit?: MeasurementUnit;
}

export const ProductConfiguratorFields: React.FC<
  ProductConfiguratorFieldsProps
> = ({
  configuratorOptions,
  currentInventoryItem,
  currentValues,
  locale,
  errors,
  sizeGuideData,
  initialGender,
  initialMeasurementUnit,
}) => {
  const currentFormValues = {
    ...currentValues.prefixedFamilyOptionsValues,
    ...currentValues.prefixedProductOptionsValues,
  };

  return (
    <>
      {configuratorOptions.map((option) => {
        return (
          <FieldSet
            key={option.key}
            data-test-attribute-name={option.key}
            data-testid="product-configurator-item"
            id={`${CONFIGURATOR_FORM_ID}_${option.key}`}
          >
            <OptionFieldDelegator
              option={option}
              filterValues={option.filterValues}
              currentValue={currentFormValues[option.key] || ''}
              locale={locale}
              errors={errors}
              currentProductTypeId={currentInventoryItem?.productTypeId}
              workId={currentInventoryItem?.workId}
              sizeGuideData={sizeGuideData}
              initialGender={initialGender}
              initialMeasurementUnit={initialMeasurementUnit}
            />
          </FieldSet>
        );
      })}
    </>
  );
};

export default ProductConfigurator;
